import { ActionTree } from 'vuex'
import { ModuleState, TownMembership } from './types'
import { eventHub } from '@/store/eventHub'
import { handleError } from '@/helpers/axios'
import { RootState } from '@/types'
import { API } from '@/helpers/axios'
import i18next from 'i18next'
import i18nextConfig from '@/helpers/i18next-config'

i18next.init(i18nextConfig)

export const actions: ActionTree<ModuleState, RootState> = {
  async fetchPending({ commit }, townId: number) {
    try {
      const response = await API.get(`api/group/${townId}/members/pending`)
      if (response.status === 200) {
        commit('handleFetchPending', {memberships: response.data, townId})
        return response.data
      }
    }
    catch(error) { handleError(error) }
  },
  async approve({ dispatch },
                { townId, member }: { townId: number; member: TownMembership; }) {
    try {
      const response = await API.post(`api/group/${townId}/member/${member.user_id}/approve`)
      if (response.status === 200) {
        eventHub.$emit('triggerSnackbar', {
          color: 'success',
          text: (`${i18next.t('Approved')} ${member.username} ${i18next.t('to join the town')}.`),
          undo: () => dispatch('unApprove', { townId, userId: member.user_id })
        })
        return response.data
      }
    }
    catch(error) { handleError(error) }
	},
  async bulkApprove({ dispatch },
                    { townId, members }: { townId: number; members: TownMembership[]; }) {
    try {
      const userIds = members.map(member => member.user_id).join(',')
      const response = await API.post(`api/group/${townId}/member/bulk/approve`, { userIds })
      if (response.status === 200) {
        return response.data
      }
    }
    catch(error) { handleError(error) }
  },
  async unApprove({ dispatch }, { townId, userId }: { townId: number; userId: number }) {
    try {
      return await dispatch('setPending', { townId, userId })
    }
    catch(error) { handleError(error) }
  },
  async reject({ dispatch },
               { townId, member, notice, comment }:
               { townId: number; member: TownMembership; notice: string; comment: string; }) {
    try {
      const response = await API.post(`api/group/${townId}/member/${member.user_id}/reject`, { notice, comment })
      if (response.status === 200) {
        eventHub.$emit('triggerSnackbar', {
          color: 'warning',
          text: (`${i18next.t('Rejected')} ${member.username} ${i18next.t('from joining the town')}.`),
          undo: () => dispatch('unReject', { townId, userId: member.user_id })
        })
        return response.data
      }
    }
    catch(error) { handleError(error) }
  },
  async bulkReject({ dispatch },
               { townId, members, notice, comment }:
               { townId: number; members: TownMembership[]; notice: string; comment: string; }) {
    try {
      const userIds = members.map(member => member.user_id).join(',')
      const response = await API.post(`api/group/${townId}/member/bulk/reject`, { notice, comment, userIds })
      if (response.status === 200) {
        return response.data
      }
    }
    catch(error) { handleError(error) }
  },
  async unReject({ dispatch }, { townId, userId }: { townId: number; userId: number }) {
    try {
      await dispatch('joinAndSetPending', { townId, userId })
      return await dispatch('fetchPending', townId)
    }
    catch(error) { handleError(error) }
  },
  async joinAndSetPending({ dispatch }, { townId, userId }: { townId: number; userId: number }) {
    try {
      const response = await API.get(`api/group/${townId}/members`)
      if (response.data.map((x: { user_id: number }) => x.user_id).includes(userId)) {
        const user = response.data.find((x: {user_id: number}) => x.user_id === userId)
        if (user !== undefined && !user.is_pending)
          return await dispatch('setPending', { townId, userId })
        else return
      } else {
        await API.post(`api/group/${townId}/join`, { userId })
        return dispatch('setPending', { townId, userId })
      }
    }
    catch(error) { handleError(error) }
  },
  async setPending(_, { townId, userId }: { townId: number; userId: number }) {
    try {
      const response = await API.post(`api/group/${townId}/member/${userId}/update`, { is_pending: 1, approved_by: 0 })
      if (response.status === 200) {
        eventHub.$emit('triggerSnackbar', {
          color: 'success',
          text: `${i18next.t('Set')} ${userId} ${i18next.t('to pending')}.`
        })
        return response.data
      }
    }
    catch(error) { handleError(error) }
  },
  async moderate({ dispatch },
                 { townId, member }: { townId: number; member: TownMembership; }) {
    try {
      const response = await API.post(`api/group/${townId}/member/${member.user_id}/moderate`)
      if (response.status === 200) {
        eventHub.$emit('triggerSnackbar', {
          color: 'success',
          text: `${i18next.t('Set')} ${member.username} ${i18next.t('to moderated')}.`,
          undo: () => dispatch('unModerate', { townId, member })
        })
        return response.data
      }
    }
    catch(error) { handleError(error) }
	},
  async bulkModerate({ dispatch },
                     { townId, members }: { townId: number; members: TownMembership[] }) {
    try {
      const userIds = members.map(member => member.user_id).join(',')
      const response = await API.post(`api/group/${townId}/member/bulk/moderate`, {userIds})
      if (response.status === 200) {
        return response.data
      }
    }
    catch(error) { handleError(error) }
  },
  async unModerate({ dispatch },
                   { townId, member }: { townId: number; member: TownMembership; }) {
    try {
      const response = await API.post(`api/group/${townId}/member/${member.user_id}/unmoderate`)
      if (response.status === 200) {
        eventHub.$emit('triggerSnackbar', {
          color: 'success',
          text: `${i18next.t('Set')} ${member.username} ${i18next.t('to unmoderated')}.`,
          undo: () => dispatch('moderate', { townId, member })
        })
        return response.data
      }
    }
    catch(error) { handleError(error) }
	},
  async bulkUnmoderate({ dispatch },
                       { townId, members }: { townId: number; members: TownMembership[] }) {
    try {
      const userIds = members.map(member => member.user_id).join(',')
      // we can't put all the IDs in the URL! that's why it's a post!
      const response = await API.post(`api/group/${townId}/member/bulk/unmoderate/`, { userIds })
      if (response.status === 200) {
        return response.data
      }
    }
    catch(error) { handleError(error) }
  },
  async tempModerate(_, { townId, member }: { townId: number; member: TownMembership; }) {
    try {
      const response = await API.post(`api/group/${townId}/member/${member.user_id}/temp-moderate`)
      if (response.status === 200) {
        eventHub.$emit('triggerSnackbar', {
          color: 'success',
          text: `${i18next.t('Set')} ${member.username} ${i18next.t('to temporarily moderated')}.`
        })
        return response.data
      }
    }
    catch(error) { handleError(error) }
	},
  async bulkTempModerate({ dispatch },
                         { townId, members }: { townId: number; members: TownMembership[] }) {
    try {
      const userIds = members.map(member => member.user_id).join(',')
      const response = await API.post(`api/group/${townId}/member/bulk/temp-moderate/`, { userIds })
      if (response.status === 200) {
        return response.data
      }
    }
    catch(error) { handleError(error) }
  },
  async setEmailDeliveryPreference(_, {townId, member, deliveryPreference }: {
    townId: number;
    member: TownMembership;
    deliveryPreference: number;
  }) {
    try {
      const response = await API
        .post(`api/group/${townId}/member/${member.user_id}/set-email-delivery-preference`, { deliveryPreference })
      if (response.status === 200) {
        eventHub.$emit('triggerSnackbar', {
          color: 'success',
          text: `${i18next.t('Successfully updated email delivery preference for')} ${member.username}.`,
          timeout: 2000
        })
        return response.data
      }
    }
    catch(error) { handleError(error) }
  },
  async bulkSetEmailDeliveryPreference(_, {townId, members, deliveryPreference }: {
    townId: number;
    members: TownMembership[];
    deliveryPreference: number;
  }) {
    try {
      const userIds = members.map(member => member.user_id).join(',')
      const response = await API
        .post(`api/group/${townId}/member/bulk/set-email-delivery-preference`, { deliveryPreference, userIds })
      return response.data
    }
    catch(error) { handleError(error) }
  },
  async remove(_,
               { townId, member }:
               { townId: number; member: TownMembership }) {
    try {
      const response = await API.post(`api/group/${townId}/member/${member.user_id}/remove`)
      if (response.status === 200) {
        eventHub.$emit('triggerSnackbar', {
          color: 'alert',
          text: `${i18next.t('Removed')} ${member.username} ${i18next.t('from the town')}.`
        })
      }
      return response.data
    }
    catch(error) { handleError(error) }
  },
  async bulkRemove(_,
                   { townId, members }:
                   { townId: number; members: TownMembership[]; }) {
    try {
      const userIds = members.map(member => member.user_id).join(',')
      const response = await API.post(`api/group/${townId}/member/bulk/remove`, { userIds })
      return response.data
    }
    catch(error) { handleError(error) }
  },
  async changeModeratorLevel(_,
                             { currentLevel, targetLevel, townId, userId }:
                             { currentLevel: number; targetLevel: number; townId: number; userId: number }) {
    try {
      const response = await API.post(`api/group/${townId}/member/${userId}/mod-level`,
                                      { currentLevel, targetLevel })
      return response.data
    }
    catch(error) { handleError(error) }
  },
  async checkInvitees(_, invitees: string[]) {
    try {
      const response = await API.post(`api/member/check`, invitees)
      return response.data
    }
    catch(error) { handleError(error) }
  },
  async invite(_, { townId, member }: { townId: number; member: TownMembership; }) {
    try {
      const response = await API.post(`api/group/${townId}/access/invite`, {
        userId: member.user_id,
        groupId: townId
      })
      if (response.status === 200) return response.data
    }
    catch(error) { handleError(error) }
	}
}
